import * as Types from '@trello/graphql/generated';

import { useQuickLoad } from '@trello/quickload';
import type { TrelloQueryHookOptions, TrelloLazyQueryHookOptions, TrelloSuspenseQueryHookOptions } from '@trello/quickload';
import DocumentNode from '@trello/graphql/src/documentNode';
import * as Apollo from '@apollo/client';
export const PublicPluginsDocument = {"kind":"Document","definitions":[{"kind":"OperationDefinition","operation":"query","name":{"kind":"Name","value":"PublicPlugins"},"variableDefinitions":[{"kind":"VariableDefinition","variable":{"kind":"Variable","name":{"kind":"Name","value":"preferredLocales"}},"type":{"kind":"NonNullType","type":{"kind":"NamedType","name":{"kind":"Name","value":"String"}}}}],"selectionSet":{"kind":"SelectionSet","selections":[{"kind":"Field","name":{"kind":"Name","value":"member"},"arguments":[{"kind":"Argument","name":{"kind":"Name","value":"id"},"value":{"kind":"StringValue","value":"me","block":false}}],"directives":[{"kind":"Directive","name":{"kind":"Name","value":"client"}}],"selectionSet":{"kind":"SelectionSet","selections":[{"kind":"Field","name":{"kind":"Name","value":"id"}},{"kind":"Field","name":{"kind":"Name","value":"organizations"},"selectionSet":{"kind":"SelectionSet","selections":[{"kind":"Field","name":{"kind":"Name","value":"id"}},{"kind":"Field","name":{"kind":"Name","value":"products"}}]}}]}},{"kind":"Field","name":{"kind":"Name","value":"publicPlugins"},"arguments":[{"kind":"Argument","name":{"kind":"Name","value":"preferredLocales"},"value":{"kind":"Variable","name":{"kind":"Name","value":"preferredLocales"}}}],"directives":[{"kind":"Directive","name":{"kind":"Name","value":"client"}}],"selectionSet":{"kind":"SelectionSet","selections":[{"kind":"Field","name":{"kind":"Name","value":"id"}},{"kind":"Field","name":{"kind":"Name","value":"author"}},{"kind":"Field","name":{"kind":"Name","value":"categories"}},{"kind":"Field","name":{"kind":"Name","value":"deprecation"},"selectionSet":{"kind":"SelectionSet","selections":[{"kind":"Field","name":{"kind":"Name","value":"infoLink"}},{"kind":"Field","name":{"kind":"Name","value":"sunsetDate"}}]}},{"kind":"Field","name":{"kind":"Name","value":"email"}},{"kind":"Field","name":{"kind":"Name","value":"heroImageUrl"}},{"kind":"Field","name":{"kind":"Name","value":"icon"},"selectionSet":{"kind":"SelectionSet","selections":[{"kind":"Field","name":{"kind":"Name","value":"url"}}]}},{"kind":"Field","name":{"kind":"Name","value":"idOrganizationOwner"}},{"kind":"Field","name":{"kind":"Name","value":"iframeConnectorUrl"}},{"kind":"Field","name":{"kind":"Name","value":"isCompliantWithPrivacyStandards"}},{"kind":"Field","name":{"kind":"Name","value":"listing"},"selectionSet":{"kind":"SelectionSet","selections":[{"kind":"Field","name":{"kind":"Name","value":"description"}},{"kind":"Field","name":{"kind":"Name","value":"name"}},{"kind":"Field","name":{"kind":"Name","value":"overview"}}]}},{"kind":"Field","name":{"kind":"Name","value":"moderatedState"}},{"kind":"Field","name":{"kind":"Name","value":"privacyUrl"}},{"kind":"Field","name":{"kind":"Name","value":"public"}},{"kind":"Field","name":{"kind":"Name","value":"supportEmail"}},{"kind":"Field","name":{"kind":"Name","value":"tags"}},{"kind":"Field","name":{"kind":"Name","value":"usageBrackets"},"selectionSet":{"kind":"SelectionSet","selections":[{"kind":"Field","name":{"kind":"Name","value":"boards"}}]}}]}}]}}]} as unknown as DocumentNode;
const defaultOptions = {"context":{"operationName":"PublicPlugins","document":PublicPluginsDocument}} as const;
export type PublicPluginsQueryVariables = Types.Exact<{
  preferredLocales: Types.Scalars['String']['input'];
}>;


export type PublicPluginsQuery = (
  { __typename: 'Query' }
  & { member?: Types.Maybe<(
    { __typename: 'Member' }
    & Pick<Types.Member, 'id'>
    & { organizations: Array<(
      { __typename: 'Organization' }
      & Pick<Types.Organization, 'id' | 'products'>
    )> }
  )>, publicPlugins: Array<(
    { __typename: 'Plugin' }
    & Pick<Types.Plugin, 'id' | 'author' | 'categories' | 'email' | 'heroImageUrl' | 'idOrganizationOwner' | 'iframeConnectorUrl' | 'isCompliantWithPrivacyStandards' | 'moderatedState' | 'privacyUrl' | 'public' | 'supportEmail' | 'tags'>
    & { deprecation?: Types.Maybe<(
      { __typename: 'Plugin_Deprecation' }
      & Pick<Types.Plugin_Deprecation, 'infoLink' | 'sunsetDate'>
    )>, icon: (
      { __typename: 'Plugin_Icon' }
      & Pick<Types.Plugin_Icon, 'url'>
    ), listing?: Types.Maybe<(
      { __typename: 'Plugin_Listing' }
      & Pick<Types.Plugin_Listing, 'description' | 'name' | 'overview'>
    )>, usageBrackets: (
      { __typename: 'Plugin_UsageBrackets' }
      & Pick<Types.Plugin_UsageBrackets, 'boards'>
    ) }
  )> }
);

/**
 * __usePublicPluginsQuery__
 *
 * To run a query within a React component, call `usePublicPluginsQuery` and pass it any options that fit your needs.
 * When your component renders, `usePublicPluginsQuery` returns an object from Apollo Client that contains loading, error, and data properties
 * you can use to render your UI.
 *
 * @param baseOptions options that will be passed into the query, supported options are listed on: https://www.apollographql.com/docs/react/api/react-hooks/#options;
 *
 * @example
 * const { data, loading, error } = usePublicPluginsQuery({
 *   variables: {
 *      preferredLocales: // value for 'preferredLocales'
 *   },
 * });
 */
export function usePublicPluginsQuery(
  baseOptions: TrelloQueryHookOptions<
    PublicPluginsQuery,
    PublicPluginsQueryVariables
  > &
    (
      | { variables: PublicPluginsQueryVariables; skip?: boolean }
      | { skip: boolean }
    ),
) {
  const isQuickLoading = useQuickLoad({
    waitOn: baseOptions?.waitOn || ['None'],
    document: PublicPluginsDocument,
    skip: baseOptions?.skip,
  });
  const options = {
    ...defaultOptions,
    ...baseOptions,
    skip: isQuickLoading || baseOptions?.skip,
  };
  const result = Apollo.useQuery<
    PublicPluginsQuery,
    PublicPluginsQueryVariables
  >(PublicPluginsDocument, options);
  // reconstruct result because modifying the useQuery result actually changes apollo behavior because of memoization
  const trelloResult = { ...result };
  if (isQuickLoading) {
    trelloResult.loading = true;
  }

  return trelloResult;
}
export function usePublicPluginsLazyQuery(
  baseOptions?: TrelloLazyQueryHookOptions<
    PublicPluginsQuery,
    PublicPluginsQueryVariables
  >,
) {
  const options = { ...defaultOptions, ...baseOptions };
  return Apollo.useLazyQuery<PublicPluginsQuery, PublicPluginsQueryVariables>(
    PublicPluginsDocument,
    options,
  );
}
export function usePublicPluginsSuspenseQuery(
  baseOptions?:
    | Apollo.SkipToken
    | TrelloSuspenseQueryHookOptions<
        PublicPluginsQuery,
        PublicPluginsQueryVariables
      >,
) {
  const options =
    baseOptions === Apollo.skipToken
      ? baseOptions
      : { ...defaultOptions, ...baseOptions };
  return Apollo.useSuspenseQuery<
    PublicPluginsQuery,
    PublicPluginsQueryVariables
  >(PublicPluginsDocument, options);
}
export type PublicPluginsQueryHookResult = ReturnType<
  typeof usePublicPluginsQuery
>;
export type PublicPluginsLazyQueryHookResult = ReturnType<
  typeof usePublicPluginsLazyQuery
>;
export type PublicPluginsSuspenseQueryHookResult = ReturnType<
  typeof usePublicPluginsSuspenseQuery
>;
export type PublicPluginsQueryResult = Apollo.QueryResult<
  PublicPluginsQuery,
  PublicPluginsQueryVariables
>;
